import React from 'react';
import Icon from 'components/AppIcon';

const WORKOUT_TYPES = [
  { id: 'strength', label: 'Strength Training', icon: 'Dumbbell', color: 'var(--color-primary)' },
  { id: 'cardio', label: 'Cardio', icon: 'Heart', color: 'var(--color-destructive)' },
  { id: 'yoga', label: 'Yoga', icon: 'Flower2', color: 'var(--color-success)' },
  { id: 'crossfit', label: 'CrossFit', icon: 'Zap', color: 'var(--color-warning)' },
  { id: 'hiit', label: 'HIIT', icon: 'Flame', color: 'var(--color-accent)' },
  { id: 'pilates', label: 'Pilates', icon: 'Activity', color: 'var(--color-secondary)' },
  { id: 'martial_arts', label: 'Martial Arts', icon: 'Shield', color: 'var(--color-primary)' },
  { id: 'swimming', label: 'Swimming', icon: 'Waves', color: '#0ea5e9' },
];

const EXPERIENCE_LEVELS = [
  { id: 'beginner', label: 'Beginner', desc: 'Just starting out', icon: 'Sprout' },
  { id: 'intermediate', label: 'Intermediate', desc: '1–3 years experience', icon: 'TrendingUp' },
  { id: 'advanced', label: 'Advanced', desc: '3+ years experience', icon: 'Award' },
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIME_SLOTS = [
  { id: 'morning', label: 'Morning', desc: '5am – 12pm', icon: 'Sunrise' },
  { id: 'afternoon', label: 'Afternoon', desc: '12pm – 5pm', icon: 'Sun' },
  { id: 'evening', label: 'Evening', desc: '5pm – 10pm', icon: 'Moon' },
];

export default function WorkoutStep({ data, onChange, errors }) {
  const handleChange = (field, value) => onChange({ ...data, [field]: value });

  const toggleWorkoutType = (id) => {
    const current = data?.workoutTypes || [];
    const updated = current?.includes(id) ? current?.filter((t) => t !== id) : [...current, id];
    handleChange('workoutTypes', updated);
  };

  const toggleDay = (day) => {
    const current = data?.preferredDays || [];
    const updated = current?.includes(day) ? current?.filter((d) => d !== day) : [...current, day];
    handleChange('preferredDays', updated);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Workout Preferences</h2>
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Tell us what you enjoy so we can tailor your fitness plan.</p>
      </div>
      {/* Workout Types */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">
          Workout Types <span className="text-[var(--color-muted-foreground)] font-normal">(select all that apply)</span>
        </label>
        {errors?.workoutTypes && <p className="text-xs text-[var(--color-destructive)] mb-2">{errors?.workoutTypes}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {WORKOUT_TYPES?.map((type) => {
            const isSelected = data?.workoutTypes?.includes(type?.id);
            return (
              <button
                key={type?.id}
                type="button"
                onClick={() => toggleWorkoutType(type?.id)}
                className={`flex flex-col items-center gap-2 p-3 rounded-[var(--radius-md)] border-2 transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-sm'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                }`}
              >
                <div
                  className="w-10 h-10 rounded-[var(--radius-sm)] flex items-center justify-center"
                  style={{ backgroundColor: isSelected ? `${type?.color}20` : 'var(--color-muted)' }}
                >
                  <Icon name={type?.icon} size={18} color={isSelected ? type?.color : 'var(--color-muted-foreground)'} strokeWidth={2} />
                </div>
                <span className={`text-xs font-medium font-[var(--font-caption)] text-center leading-tight ${
                  isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                }`}>{type?.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Experience Level */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">Experience Level</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {EXPERIENCE_LEVELS?.map((level) => {
            const isSelected = data?.experienceLevel === level?.id;
            return (
              <button
                key={level?.id}
                type="button"
                onClick={() => handleChange('experienceLevel', level?.id)}
                className={`flex items-center gap-3 p-4 rounded-[var(--radius-md)] border-2 transition-all duration-200 text-left ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-muted)]'
                }`}>
                  <Icon name={level?.icon} size={16} color={isSelected ? '#fff' : 'var(--color-muted-foreground)'} />
                </div>
                <div>
                  <p className={`text-sm font-semibold font-[var(--font-caption)] ${
                    isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                  }`}>{level?.label}</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{level?.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Preferred Days */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">Preferred Workout Days</label>
        <div className="flex flex-wrap gap-2">
          {DAYS?.map((day) => {
            const isSelected = data?.preferredDays?.includes(day);
            return (
              <button
                key={day}
                type="button"
                onClick={() => toggleDay(day)}
                className={`w-12 h-12 rounded-full text-sm font-semibold font-[var(--font-caption)] border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                    : 'border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/50 bg-[var(--color-card)]'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
      {/* Preferred Time */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">Preferred Workout Time</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TIME_SLOTS?.map((slot) => {
            const isSelected = data?.preferredTime === slot?.id;
            return (
              <button
                key={slot?.id}
                type="button"
                onClick={() => handleChange('preferredTime', slot?.id)}
                className={`flex items-center gap-3 p-4 rounded-[var(--radius-md)] border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                }`}
              >
                <Icon name={slot?.icon} size={20} color={isSelected ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                <div className="text-left">
                  <p className={`text-sm font-semibold font-[var(--font-caption)] ${
                    isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                  }`}>{slot?.label}</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{slot?.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
